/**
 * A function that takes any number of arguments and returns any value.
 */
type AnyFunction = (...args: any[]) => any;
/**
 * This type represents a debounced version of a function.
 * It takes a generic type parameter T, which extends AnyFunction.
 *
 * The DebouncedFunction type itself is a function that takes the same parameters
 * as the original function T (using Parameters<T>) but returns void.
 * This means that the debounced function will not return any value,
 * even if the original function T does.
 */
type DebouncedFunction<T extends AnyFunction> = (...args: Parameters<T>) => void;

/**
 * The debounce function delays the processing of the given function until
 * it is not called for a predetermined amount of time.
 *
 * @param fn the function to delay
 * @param ms amount of time in milliseconds to wait for calls
 * @returns a function wrapper which debounces the `fn` function
 */
export const debounce = <T extends AnyFunction>(fn: T, ms: number): DebouncedFunction<T> => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    return (...args: Parameters<T>) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), ms);
    };
}

/**
 * This type extends DebouncedFunction<T> by adding an additional method, setDelay.
 *
 * By combining the debounced function with the setDelay method, the DynamicDebouncedFunction type provides a more flexible
 * and dynamic debouncing mechanism.
 */
type DynamicDebouncedFunction<T extends AnyFunction> = DebouncedFunction<T> & {
    /**
     * Set the dynamically the delay time for the debounced function.
     *
     * @param ms the new delay time in milliseconds
     */
    setDelay(ms: number): void;
};

/**
 * The debounce function delays the processing of the given function until
 * it is not called for a predetermined amount of time.
 *
 * It allows to change the delay time dynamically and
 * execute the function immediately if the remaining time is zero or negative.
 *
 * @param fn the function to delay
 * @param ms amount of time in milliseconds to wait for calls
 * @returns a function wrapper which debounces the `fn` function and allows to change the delay time
 */
export const debounceDynamic = <T extends AnyFunction>(fn: T, ms: number): DynamicDebouncedFunction<T> => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let delay = ms;
    let lastCallTime: number | null = null;
    let pendingArgs: Parameters<T> | null = null;

    const schedule = () => {
        const now = Date.now();
        if (lastCallTime === null) {
            lastCallTime = now;
        }
        const elapsed = now - lastCallTime;
        const remainingTime = Math.max(0, delay - elapsed);

        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
            pendingArgs && fn(...pendingArgs);
            timeoutId = undefined;
            lastCallTime = null;
            pendingArgs = null;
        }, remainingTime);
    };

    const debouncedFunction = (...args: Parameters<T>) => {
        pendingArgs = args;
        schedule();
    };

    debouncedFunction.setDelay = (ms: number) => {
        delay = ms;
        if (timeoutId) {
            schedule();
        }
    };

    return debouncedFunction;
};
